import { Leer } from "@/components/ui"
import { gruppiere, jahrLabel, kwKurz, stat, type AuswertungsDaten, type Filter, type Stat } from "@/lib/auswertung"
import { farbe, mmss, nf0, nf1, pf1 } from "@/lib/format"

interface Props {
  d: AuswertungsDaten
  tp: Float64Array
  ids: number[]
  f: Filter
  ausgeblendet: number
}

function Sparkline({ werte }: { werte: number[] }) {
  const W = 168, H = 40, p = 5
  const lo = Math.min(...werte), hi = Math.max(...werte)
  const x = (i: number) => p + (i / Math.max(1, werte.length - 1)) * (W - 2 * p)
  const y = (v: number) => (hi === lo ? H / 2 : H - p - ((v - lo) / (hi - lo)) * (H - 2 * p))
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true" className="block">
      <path
        d={werte.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join("")}
        fill="none" stroke={farbe("deemph")} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round"
      />
      {werte.map((v, i) => {
        const letzter = i === werte.length - 1
        return (
          <circle key={i} cx={x(i)} cy={y(v)} r={letzter ? 4 : 2.5}
            fill={letzter ? farbe("accent") : farbe("deemph")}
            stroke={farbe("surface")} strokeWidth={letzter ? 2 : 0} />
        )
      })}
    </svg>
  )
}

/** Veränderung letztes ggü. vorletztes Jahr der Auswahl; Farbe nach „mehr ist gut/schlecht“. */
function Delta({ d, jahre, k, gut }: { d: AuswertungsDaten; jahre: Array<{ y: number; s: Stat }>; k: keyof Stat; gut: number }) {
  const letzt = jahre[jahre.length - 1], vor = jahre[jahre.length - 2]
  if (!letzt || !vor) return null
  const a = letzt.s[k], b = vor.s[k]
  if (a == null || !b) return null
  const delta = ((a - b) / b) * 100
  const flach = Math.abs(delta) < 0.05
  const farbeKlasse = flach || !gut ? "text-ink2" : (delta > 0) === (gut > 0) ? "text-good" : "text-crit"
  const pfeil = flach ? "■" : delta > 0 ? "▲" : "▼"
  return (
    <div className="flex flex-wrap items-baseline gap-1.5 text-[12.5px] font-medium">
      <span className={farbeKlasse}>{`${pfeil} ${pf1.format(delta)} %`}</span>
      <span className="font-normal text-muted">{`${jahrLabel(d, letzt.y)} ggü. ${jahrLabel(d, vor.y)}`}</span>
    </div>
  )
}

function Kachel({ hero, children }: { hero?: boolean; children: React.ReactNode }) {
  return (
    <div className={`grid min-w-0 content-start gap-1 rounded-[10px] border border-hair/10 bg-surface px-4 py-3.5 ${hero ? "sm:col-span-3 xl:col-span-1" : ""}`}>
      {children}
    </div>
  )
}

export function Kennzahlen({ d, tp, ids, f, ausgeblendet }: Props) {
  if (!ids.length) return <Leer />
  const st = stat(d, tp, ids, f.kw)
  const proJahr = [...gruppiere(ids, (i) => d.rows.y[i])]
    .sort((a, b) => a[0] - b[0])
    .map(([y, g]) => ({ y, s: stat(d, tp, g, f.kw) }))
  const einheit = "ml-1 text-[0.5em] font-medium text-ink2"
  return (
    <section aria-label="Kennzahlen" className="grid grid-cols-1 gap-3 sm:grid-cols-3 xl:grid-cols-[2fr_1fr_1fr_1fr]">
      <Kachel hero>
        <div className="text-[12.5px] text-ink2">{`Durchsatz NC feucht (${kwKurz(f.kw)})`}</div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="text-5xl font-semibold leading-none tracking-tight">
            {nf0.format(st.tp ?? 0)}<span className="ml-1 text-lg font-medium text-ink2">kg/h</span>
          </div>
          {proJahr.length > 1 && <Sparkline werte={proJahr.map((p) => p.s.tp ?? 0)} />}
        </div>
        <div className="text-xs text-muted">
          {`aus ${nf0.format(st.n)} Proben · ${f.ausr ? "Ausreißer einbezogen" : `${nf0.format(ausgeblendet)} Ausreißer ausgeblendet`}`}
        </div>
        <Delta d={d} jahre={proJahr} k="tp" gut={1} />
      </Kachel>
      <Kachel>
        <div className="text-[12.5px] text-ink2">Zykluszeit</div>
        <div className="text-[26px] font-semibold leading-tight">{mmss(st.z ?? 0)}<span className={einheit}>min</span></div>
        <div className="text-xs text-muted">{`${nf0.format(st.z ?? 0)} s`}</div>
        <Delta d={d} jahre={proJahr} k="z" gut={-1} />
      </Kachel>
      <Kachel>
        <div className="text-[12.5px] text-ink2">Auswurfgewicht</div>
        <div className="text-[26px] font-semibold leading-tight">{nf1.format(st.aw ?? 0)}<span className={einheit}>kg</span></div>
        <Delta d={d} jahre={proJahr} k="aw" gut={1} />
      </Kachel>
      <Kachel>
        <div className="text-[12.5px] text-ink2">Füllgewicht</div>
        <div className="text-[26px] font-semibold leading-tight">{st.f == null ? "–" : nf1.format(st.f)}<span className={einheit}>kg</span></div>
        <Delta d={d} jahre={proJahr} k="f" gut={0} />
      </Kachel>
    </section>
  )
}
